import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  CheckSquare,
  Briefcase,
  ListTodo,
  Clock,
  Users,
  BarChart3,
  Building2,
  TrendingUp,
  DollarSign,
  SearchCode,
  CalendarCheck2,
  ChevronLeft,
  ChevronRight,
  Settings,
  Cpu,
  X,
  Sun,
  Moon,
  Activity,
  Megaphone,
  AlertOctagon,
} from 'lucide-react';
import { cn } from '@/lib/utils.js';
import { useAuth } from '@/context/AuthContext.js';
import { useBranding } from '@/context/BrandingContext.js';

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  mobileOpen?: boolean;
  onCloseMobile?: () => void;
}

const primaryNavItems = [
  { label: 'Overview', icon: LayoutDashboard, href: '/dashboard' },
  { label: 'My Tasks', icon: CheckSquare, href: '/my-tasks' },
  { label: 'Projects', icon: Briefcase, href: '/projects' },
  { label: 'Tasks', icon: ListTodo, href: '/tasks' },
  { label: 'Blockers', icon: AlertOctagon, href: '/blockers' },
  { label: 'Time', icon: Clock, href: '/time' },
  { label: 'Team', icon: Users, href: '/team' },
  { label: 'Reports', icon: BarChart3, href: '/reports' },
  { label: 'Activity Logs', icon: Activity, href: '/activity' },
];

const agencyNavItems = [
  { label: 'Clients', icon: Building2, href: '/clients' },
  { label: 'Leads', icon: TrendingUp, href: '/leads' },
  { label: 'Promotions', icon: Megaphone, href: '/promotions' },
  { label: 'Finance', icon: DollarSign, href: '/finance' },
  { label: 'SEO', icon: SearchCode, href: '/seo' },
  { label: 'Standups', icon: CalendarCheck2, href: '/standups' },
];

export function Sidebar({ collapsed, onToggle, mobileOpen = false, onCloseMobile }: SidebarProps) {
  const location = useLocation();
  const { user } = useAuth();
  const { branding } = useBranding();
  const role = user?.role || 'team_member';

  const isDevOrTeamMember = role === 'developer' || role === 'team_member';
  const isViewer = role === 'viewer';
  const isOwner = role === 'owner';

  // Sidebar theme: 'light' (modern surface) vs 'dark' (executive deep ink #073B38)
  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    return (localStorage.getItem('tsos_sidebar_theme') as 'light' | 'dark') || 'light';
  });
  const [logoError, setLogoError] = useState(false);

  // Reset error state when custom logo URL updates
  useEffect(() => {
    setLogoError(false);
  }, [branding.logo_url, branding.dark_logo_url]);

  const toggleTheme = () => {
    const next = theme === 'light' ? 'dark' : 'light';
    setTheme(next);
    localStorage.setItem('tsos_sidebar_theme', next);
  };

  const visiblePrimaryItems = primaryNavItems.filter((item) => {
    // Time and Team are disallowed for viewers
    if ((item.href === '/time' || item.href === '/team') && isViewer) return false;
    return true;
  });

  const visibleAgencyItems = agencyNavItems.filter((item) => {
    // Finance: Owner ONLY
    if (item.href === '/finance' && !isOwner) return false;
    // Clients, Leads & Promotions: Owner and Manager ONLY (Developers, Team Members, Viewers hidden)
    if ((item.href === '/clients' || item.href === '/leads' || item.href === '/promotions') && (isDevOrTeamMember || isViewer)) return false;
    // SEO and Standups: Owner, Manager, Developers, Team Members (Viewers hidden)
    if ((item.href === '/standups' || item.href === '/seo') && isViewer) return false;
    return true;
  });

  const isDark = theme === 'dark';

  const sidebarContent = (isMobileDrawer: boolean) => (
    <div
      className={cn(
        'flex flex-col h-full select-none transition-colors duration-normal',
        isDark
          ? 'bg-[#073B38] text-[#EFFFF9]'
          : 'bg-surface text-brand-ink'
      )}
    >
      {/* Brand Header */}
      <div
        className={cn(
          'flex h-16 items-center justify-between px-4 border-b transition-colors',
          isDark ? 'border-[#D2E1DB]/15' : 'border-brand-border'
        )}
      >
        {(() => {
          const customLogoUrl = isDark && branding.dark_logo_url ? branding.dark_logo_url : (branding.logo_url || branding.dark_logo_url);
          const hasCustomLogo = Boolean(customLogoUrl && !logoError);

          return (!collapsed || isMobileDrawer) ? (
            <div className="flex items-center space-x-3 overflow-hidden">
              {hasCustomLogo ? (
                <div className="relative h-9 w-9 shrink-0 rounded-xl border border-brand-border/40 flex items-center justify-center p-0.5 bg-white/5 shadow-sm overflow-hidden">
                  <img
                    src={customLogoUrl!}
                    alt={branding.workspace_name || 'Logo'}
                    className="h-full w-full object-contain rounded-lg"
                    onError={() => setLogoError(true)}
                  />
                </div>
              ) : (
                <div className="relative h-9 w-9 shrink-0 rounded-xl bg-gradient-to-br from-[#087F75] to-[#06685F] border border-[#C5EF79]/40 flex items-center justify-center font-black text-white text-xs tracking-wider shadow-md">
                  <span>TS</span>
                  <span className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-[#C5EF79] ring-2 ring-[#073B38]" />
                </div>
              )}
              <div className="flex flex-col min-w-0">
                <span className={cn('font-bold text-sm tracking-tight truncate', isDark ? 'text-[#EFFFF9]' : 'text-brand-ink')}>
                  {branding.workspace_name || 'Techosolution'}
                </span>
                <span className={cn('text-[10px] font-semibold tracking-wider uppercase flex items-center gap-1.5', isDark ? 'text-[#D2EBE4]/80' : 'text-brand-muted')}>
                  <span className="h-1.5 w-1.5 rounded-full bg-[#C5EF79] animate-pulse" />
                  Agency OS
                </span>
              </div>
            </div>
          ) : (
            hasCustomLogo ? (
              <div
                className="mx-auto relative h-9 w-9 rounded-xl border border-brand-border/40 flex items-center justify-center p-0.5 bg-white/5 shadow-sm overflow-hidden"
                title={branding.workspace_name || 'Techosolution'}
              >
                <img
                  src={customLogoUrl!}
                  alt={branding.workspace_name || 'Logo'}
                  className="h-full w-full object-contain rounded-lg"
                  onError={() => setLogoError(true)}
                />
              </div>
            ) : (
              <div className="mx-auto relative h-9 w-9 rounded-xl bg-gradient-to-br from-[#087F75] to-[#06685F] border border-[#C5EF79]/40 flex items-center justify-center font-black text-white text-xs shadow-md">
                <span>TS</span>
                <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-[#C5EF79] ring-2 ring-[#073B38]" />
              </div>
            )
          );
        })()}

        {/* Mobile close button */}
        {isMobileDrawer && (
          <button
            onClick={onCloseMobile}
            className={cn(
              'p-1.5 rounded-lg transition-colors',
              isDark ? 'text-[#D2EBE4] hover:bg-[#087F75]/30' : 'text-brand-muted hover:text-brand-ink hover:bg-brand-surface'
            )}
            title="Close menu"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      {/* Navigation List */}
      <nav className="flex-1 space-y-5 px-3 py-4 overflow-y-auto">
        {/* Core Workspace Group */}
        <div className="space-y-1">
          {(!collapsed || isMobileDrawer) && (
            <div className={cn('px-3 pb-1 text-[10px] font-bold tracking-widest uppercase', isDark ? 'text-[#D2EBE4]/60' : 'text-brand-muted')}>
              Workspace
            </div>
          )}
          {visiblePrimaryItems.map((item) => {
            const isActive = location.pathname.startsWith(item.href);
            const Icon = item.icon;
            return (
              <Link
                key={item.href}
                to={item.href}
                onClick={isMobileDrawer ? onCloseMobile : undefined}
                title={collapsed && !isMobileDrawer ? item.label : undefined}
                className={cn(
                  'group relative flex items-center rounded-xl text-sm font-medium transition-all duration-fast',
                  isActive
                    ? 'bg-[#087F75] text-[#EFFFF9] shadow-sm font-semibold'
                    : isDark
                    ? 'text-[#D2EBE4]/80 hover:bg-[#087F75]/25 hover:text-[#EFFFF9]'
                    : 'text-brand-muted hover:bg-brand-tint/50 hover:text-brand-ink',
                  collapsed && !isMobileDrawer ? 'h-10 w-10 mx-auto justify-center p-0' : 'px-3 py-2.5 space-x-3'
                )}
              >
                {isActive && (
                  <span
                    className="absolute left-0 top-2 bottom-2 w-1 rounded-r bg-[#C5EF79] transition-all"
                  />
                )}
                <Icon
                  className={cn(
                    'h-4 w-4 shrink-0 transition-colors',
                    isActive
                      ? 'text-[#C5EF79]'
                      : isDark
                      ? 'text-[#D2EBE4]/75 group-hover:text-[#EFFFF9]'
                      : 'text-brand-muted group-hover:text-brand'
                  )}
                />
                {(!collapsed || isMobileDrawer) && <span className="truncate">{item.label}</span>}
              </Link>
            );
          })}
        </div>

        {/* Agency Group */}
        {visibleAgencyItems.length > 0 && (
          <div className={cn('space-y-1 pt-3 border-t', isDark ? 'border-[#D2E1DB]/10' : 'border-brand-border')}>
            {(!collapsed || isMobileDrawer) && (
              <div className={cn('px-3 pb-1 text-[10px] font-bold tracking-widest uppercase', isDark ? 'text-[#D2EBE4]/60' : 'text-brand-muted')}>
                Agency Suite
              </div>
            )}
            {visibleAgencyItems.map((item) => {
              const isActive = location.pathname.startsWith(item.href);
              const Icon = item.icon;
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  onClick={isMobileDrawer ? onCloseMobile : undefined}
                  title={collapsed && !isMobileDrawer ? item.label : undefined}
                  className={cn(
                    'group relative flex items-center rounded-xl text-sm font-medium transition-all duration-fast',
                    isActive
                      ? 'bg-[#087F75] text-[#EFFFF9] shadow-sm font-semibold'
                      : isDark
                      ? 'text-[#D2EBE4]/80 hover:bg-[#087F75]/25 hover:text-[#EFFFF9]'
                      : 'text-brand-muted hover:bg-brand-tint/50 hover:text-brand-ink',
                    collapsed && !isMobileDrawer ? 'h-10 w-10 mx-auto justify-center p-0' : 'px-3 py-2.5 space-x-3'
                  )}
                >
                  {isActive && (
                    <span
                      className="absolute left-0 top-2 bottom-2 w-1 rounded-r bg-[#C5EF79] transition-all"
                    />
                  )}
                  <Icon
                    className={cn(
                      'h-4 w-4 shrink-0 transition-colors',
                      isActive
                        ? 'text-[#C5EF79]'
                        : isDark
                        ? 'text-[#D2EBE4]/75 group-hover:text-[#EFFFF9]'
                        : 'text-brand-muted group-hover:text-brand'
                    )}
                  />
                  {(!collapsed || isMobileDrawer) && <span className="truncate">{item.label}</span>}
                </Link>
              );
            })}
          </div>
        )}
      </nav>

      {/* Settings Navigation (Owner ONLY) */}
      {isOwner && (
        <div className={cn('px-3 py-2 border-t space-y-1', isDark ? 'border-[#D2E1DB]/10' : 'border-brand-border')}>
          <Link
            to="/mcp-settings"
            onClick={isMobileDrawer ? onCloseMobile : undefined}
            title={collapsed && !isMobileDrawer ? 'MCP Tools' : undefined}
            className={cn(
              'group relative flex items-center rounded-xl text-sm font-medium transition-all duration-fast',
              location.pathname.startsWith('/mcp-settings')
                ? 'bg-[#087F75] text-[#EFFFF9] shadow-sm font-semibold'
                : isDark
                ? 'text-[#D2EBE4]/80 hover:bg-[#087F75]/25 hover:text-[#EFFFF9]'
                : 'text-brand-muted hover:bg-brand-tint/50 hover:text-brand-ink',
              collapsed && !isMobileDrawer ? 'h-10 w-10 mx-auto justify-center p-0' : 'px-3 py-2.5 space-x-3'
            )}
          >
            {location.pathname.startsWith('/mcp-settings') && (
              <span className="absolute left-0 top-2 bottom-2 w-1 rounded-r bg-[#C5EF79]" />
            )}
            <Cpu
              className={cn(
                'h-4 w-4 shrink-0 transition-colors',
                location.pathname.startsWith('/mcp-settings')
                  ? 'text-[#C5EF79]'
                  : isDark
                  ? 'text-[#D2EBE4]/75 group-hover:text-[#EFFFF9]'
                  : 'text-brand-muted group-hover:text-brand'
              )}
            />
            {(!collapsed || isMobileDrawer) && <span className="truncate">MCP Tools</span>}
          </Link>

          <Link
            to="/settings"
            onClick={isMobileDrawer ? onCloseMobile : undefined}
            title={collapsed && !isMobileDrawer ? 'Settings & API' : undefined}
            className={cn(
              'group relative flex items-center rounded-xl text-sm font-medium transition-all duration-fast',
              location.pathname.startsWith('/settings')
                ? 'bg-[#087F75] text-[#EFFFF9] shadow-sm font-semibold'
                : isDark
                ? 'text-[#D2EBE4]/80 hover:bg-[#087F75]/25 hover:text-[#EFFFF9]'
                : 'text-brand-muted hover:bg-brand-tint/50 hover:text-brand-ink',
              collapsed && !isMobileDrawer ? 'h-10 w-10 mx-auto justify-center p-0' : 'px-3 py-2.5 space-x-3'
            )}
          >
            {location.pathname.startsWith('/settings') && (
              <span className="absolute left-0 top-2 bottom-2 w-1 rounded-r bg-[#C5EF79]" />
            )}
            <Settings
              className={cn(
                'h-4 w-4 shrink-0 transition-colors',
                location.pathname.startsWith('/settings')
                  ? 'text-[#C5EF79]'
                  : isDark
                  ? 'text-[#D2EBE4]/75 group-hover:text-[#EFFFF9]'
                  : 'text-brand-muted group-hover:text-brand'
              )}
            />
            {(!collapsed || isMobileDrawer) && <span className="truncate">Settings & API</span>}
          </Link>
        </div>
      )}

      {/* Footer Controls: Theme Toggle & Desktop Collapse */}
      <div className={cn('p-2.5 border-t space-y-1', isDark ? 'border-[#D2E1DB]/15' : 'border-brand-border')}>
        {/* Sidebar Appearance Switcher */}
        {(!collapsed || isMobileDrawer) && (
          <button
            onClick={toggleTheme}
            className={cn(
              'w-full flex items-center justify-between px-3 py-1.5 rounded-lg text-xs font-medium transition-colors',
              isDark
                ? 'text-[#D2EBE4]/70 hover:text-[#EFFFF9] hover:bg-[#087F75]/30'
                : 'text-brand-muted hover:text-brand-ink hover:bg-brand-tint/40'
            )}
            title="Toggle sidebar theme (Surface vs Deep Ink)"
          >
            <span className="flex items-center gap-2">
              {isDark ? <Moon className="h-3.5 w-3.5 text-[#C5EF79]" /> : <Sun className="h-3.5 w-3.5 text-brand" />}
              <span>{isDark ? 'Deep Ink Theme' : 'Light Surface Theme'}</span>
            </span>
            <span className={cn('text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded', isDark ? 'bg-[#042725] text-[#C5EF79]' : 'bg-brand-tint text-brand-ink')}>
              {isDark ? 'Dark' : 'Light'}
            </span>
          </button>
        )}

        {/* Desktop Collapse Toggle */}
        {!isMobileDrawer && (
          <button
            onClick={onToggle}
            className={cn(
              'flex items-center rounded-xl transition-all duration-fast',
              isDark
                ? 'text-[#D2EBE4]/80 hover:text-[#EFFFF9] hover:bg-[#087F75]/30'
                : 'text-brand-muted hover:text-brand-ink hover:bg-brand-tint/40',
              collapsed
                ? 'h-10 w-10 mx-auto justify-center p-0'
                : 'w-full justify-between px-3 py-2 text-xs font-semibold'
            )}
            title={collapsed ? 'Expand sidebar (⌘/)' : 'Collapse sidebar (⌘/)'}
          >
            {collapsed ? (
              <ChevronRight className="h-4 w-4 text-[#087F75]" />
            ) : (
              <>
                <span className="flex items-center gap-2">
                  <ChevronLeft className="h-4 w-4 text-[#087F75]" />
                  <span>Collapse</span>
                </span>
                <span className={cn('rounded px-1.5 py-0.5 text-[10px] font-mono', isDark ? 'bg-[#042725] text-[#D2EBE4]/60' : 'bg-brand-tint text-brand-muted')}>
                  ⌘/
                </span>
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop In-Flow Sidebar */}
      <aside
        className={cn(
          'hidden md:flex relative flex-col border-r transition-all duration-normal select-none z-20 shrink-0 shadow-sm',
          isDark ? 'border-[#D2E1DB]/15' : 'border-brand-border',
          collapsed ? 'w-16' : 'w-64'
        )}
      >
        {sidebarContent(false)}
      </aside>

      {/* Mobile Slide-over Drawer Backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-brand-ink/40 backdrop-blur-sm z-40 md:hidden transition-opacity"
          onClick={onCloseMobile}
        />
      )}

      {/* Mobile Slide-over Drawer Container */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-72 max-w-[85vw] border-r md:hidden shadow-2xl transition-transform duration-normal ease-out',
          isDark ? 'border-[#D2E1DB]/20 bg-[#073B38]' : 'border-brand-border bg-surface',
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        {sidebarContent(true)}
      </aside>
    </>
  );
}
